import type { Log } from "@slot/presentation";
/**
 * Fetches the info-page HTML template, with a two-tier fallback chain:
 *
 * 1. **CDN** — `{baseUrl}/{templateName}.html`
 * 2. **Bundled fallback** — the built bundle's raw `info/html/template.html` (`bundledUrl`), fetched like tier 1.
 *
 * The template is locale-agnostic — translatable strings live in the per-locale
 * `game.<locale>.json` files and are substituted at parse time via `LocalizationService.t()`
 * (wired in `ShellInfoPagePresenter`). One template, many languages.
 *
 * A successful response is memoised for the lifetime of the fetcher instance — re-opening the
 * info panel reads from cache. Failed fetches are NOT cached, so a transient network blip doesn't
 * permanently poison the cache.
 *
 * Cache busting: when `cacheBustToken` is set (typically the build's `gameVersion`), it's appended
 * as `?v={token}` to the CDN URL so a build bump invalidates stale CDN caches.
 */
export interface InfoContentFetcherInput {
  /**
   * CDN root, e.g. `https://cdn.example.com/games/burning-coins/info`. Trailing slashes are
   * stripped. When empty (offline dev / no operator manifest), tier 1 is skipped.
   */
  baseUrl: string;

  /** Template filename (without `.html` extension). Defaults to `"template"`. */
  templateName: string;

  /** URL of the bundled fallback template (`AssetRegistry.resolveUrl`); null when the build has none. */
  bundledUrl: string | null;

  /** Optional cache-busting query parameter — typically `BootLaunchPayload.gameVersion`. */
  cacheBustToken?: string;

  /**
   * Per-request deadline in milliseconds, default 10 s.
   *
   * Tier 2 is only reached once tier 1 RESOLVES, so an operator CDN that accepts the connection and
   * never answers used to mean the bundled template — the fallback that exists for exactly this —
   * was never consulted, and the player sat on a regulatory page that was a title over nothing.
   */
  requestTimeoutMs?: number;

  /** Diagnostic log — wired to `services.log` for trace consistency with the rest of the runtime. */
  log?: Log;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

export class InfoContentFetcher {
  private cachedHtml: string | null = null;

  constructor(private readonly input: InfoContentFetcherInput) {}

  /** Returns the HTML template string to feed into `parseInfoHtml`, or `null` when both tiers failed. */
  public async fetchHtml(): Promise<string | null> {
    if (this.cachedHtml !== null) return this.cachedHtml;

    const cdnHtml = await this.tryFetchFromCdn();
    if (cdnHtml !== null) {
      this.cachedHtml = cdnHtml;
      return cdnHtml;
    }

    const bundledHtml = await this.tryLoadBundled();
    if (bundledHtml !== null) {
      this.cachedHtml = bundledHtml;
      return bundledHtml;
    }

    this.log(`all tiers failed — template="${this.input.templateName}" bundled="${this.input.bundledUrl ?? "none"}"`);
    return null;
  }

  private async tryFetchFromCdn(): Promise<string | null> {
    if (this.input.baseUrl.length === 0) return null;
    return this.fetchText(this.buildUrl());
  }

  private async tryLoadBundled(): Promise<string | null> {
    if (!this.input.bundledUrl) return null;
    return this.fetchText(this.input.bundledUrl);
  }

  private async fetchText(url: string): Promise<string | null> {
    try {
      const response = await fetch(url, { credentials: "omit", signal: AbortSignal.timeout(this.input.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS) });
      if (!response.ok) {
        this.log(`HTTP ${response.status} on ${url}`);
        return null;
      }
      const text = await response.text();
      if (text.length === 0) {
        this.log(`empty body on ${url}`);
        return null;
      }
      return text;
    } catch (err) {
      this.log(`fetch failed for ${url}: ${err instanceof Error ? err.message : String(err)}`);
      return null;
    }
  }

  private buildUrl(): string {
    const base = this.input.baseUrl.replace(/\/+$/, "");
    const cacheBust = this.input.cacheBustToken ? `?v=${encodeURIComponent(this.input.cacheBustToken)}` : "";
    return `${base}/${this.input.templateName}.html${cacheBust}`;
  }

  private log(message: string): void {
    this.input.log?.(`[InfoContentFetcher] ${message}`);
  }
}
